import React from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';

import {
  BodySmallText,
  CaptionText,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';
import Currency from '@prisjakt/ui-currency';
import { ProductName } from '../product-name';
import { DealBadge } from '../deal-badge';
import { Price } from '../price';

import {
  MainContent,
  RowWrapper,
  PriceTextWrapper,
  RatingDesktop,
  RatingMobile,
  Tag,
} from './styles';

import { BaseProps } from '../../types';

type Props = Pick<
  BaseProps,
  | 'name'
  | 'highlightedName'
  | 'rank'
  | 'rating'
  | 'reviewCount'
  | 'diffPercentage'
  | 'price'
  | 'priceInclShipping'
  | 'properties'
  | 'priceCount'
  | 'showFromPrice'
  | 'store'
>;

export const ListCardContent: React.FC<Props> = props => {
  const {
    name,
    highlightedName,
    rank,
    rating,
    reviewCount,
    diffPercentage = 0,
    price,
    priceInclShipping,
    properties,
    showFromPrice,
    store,
  } = props;

  const { t } = useAppContext();

  const prettyProperties = properties?.length
    ? properties
        .filter(p => p.isCore)
        .map(p => p.pretty)
        .filter(x => x)
    : [];

  return (
    <MainContent>
      <ProductName>{highlightedName || name}</ProductName>

      <BodySmallText emphasis={FontEmphasis.MEDIUM}>
        {prettyProperties.join(', ')}
      </BodySmallText>

      <RowWrapper>
        {rank && rank > 0 && (
          <Tag>
            <CaptionText emphasis={FontEmphasis.MEDIUM}>
              {t('product', 'rank.position', {
                rank,
              })}
            </CaptionText>
          </Tag>
        )}

        {rating ? (
          <>
            <RatingDesktop
              rate={rating}
              count={reviewCount}
              showCounter
              showCounterAs="number"
              showRate
              showRateAs="number"
            />
            <RatingMobile
              rate={rating}
              count={reviewCount}
              showCounter
              showCounterAs="number"
              showRate
              showRateAs="number"
              singleStar
            />
          </>
        ) : null}
      </RowWrapper>

      <RowWrapper>
        {diffPercentage ? (
          <DealBadge diffPercentage={diffPercentage} position="relative" />
        ) : null}

        {price && (
          <PriceTextWrapper gap={0.5}>
            {showFromPrice && (
              <>
                <BodySmallText emphasis={FontEmphasis.LOW}>
                  {t('product', 'price.from-abbr')}
                </BodySmallText>{' '}
              </>
            )}
            <Price
              price={price}
              isDeal={Boolean(diffPercentage)}
              currency={store?.currency}
            />
            {priceInclShipping ? (
              <BodySmallText emphasis={FontEmphasis.LOW}>
                {t('product', 'price.including-shipping', {
                  price: (
                    <Currency
                      key={priceInclShipping}
                      price={priceInclShipping}
                    />
                  ),
                })}
              </BodySmallText>
            ) : null}
          </PriceTextWrapper>
        )}
      </RowWrapper>
    </MainContent>
  );
};
